// =============================================================================
// RSL Formatter — RoutineNode definitions → human-readable script
// =============================================================================
import type { RoutineNode, AttackNode, ConditionalBonusDamageNode, BonusAttackNode } from '../../../backend/shared/types/index';
import type { RuleSystem } from '../../../backend/shared/types/index';

export function formatRoutineToRSL(
  name: string,
  gameSystem: RuleSystem,
  definition: RoutineNode[],
): string {
  const lines: string[] = [];
  lines.push(`ROUTINE "${name}"`);
  lines.push(`SYSTEM ${gameSystem}`);
  lines.push('');
  for (const node of definition) {
    if (node.type === 'attack') {
      lines.push(formatAttackNode(node as AttackNode));
      lines.push('');
    } else if (node.type === 'conditional_damage') {
      lines.push(formatConditionalBonusNode(node as ConditionalBonusDamageNode));
      lines.push('');
    } else if (node.type === 'bonus_attack') {
      lines.push(formatBonusAttackNode(node as BonusAttackNode));
      lines.push('');
    }
  }
  return lines.join('\n').trim();
}

// ---------------------------------------------------------------------------
// Attack node
// ---------------------------------------------------------------------------

function formatAttackNode(a: AttackNode): string {
  const lines: string[] = [];
  lines.push(`ATTACK "${a.label}" {`);

  // roll line
  lines.push(`  roll: ${formatAttackRoll(a)} vs: ${a.vsTarget}`);

  // crit line — 'no_crit' keyword when crits are suppressed
  if (a.noCrit) {
    lines.push(`  no_crit`);
  } else {
    const critMin = a.critRange?.[0] ?? 20;
    lines.push(`  crit: ${critMin}-20`);
  }

  for (const prop of a.weaponProperties ?? []) {
    lines.push(`  property: ${prop}`);
  }

  // on_hit damage
  if (a.damage?.length) {
    const dmg = a.damage.map(formatDamageNode).join(' + ');
    lines.push(`  on_hit:  DAMAGE { ${dmg} }`);
  }

  // on_crit extra damage
  if (a.onCritExtra?.length) {
    const dmg = a.onCritExtra.map(formatDamageNode).join(' + ');
    lines.push(`  on_crit: DAMAGE { ${dmg} }`);
  }

  lines.push('}');
  return lines.join('\n');
}

function formatAttackRoll(a: AttackNode): string {
  const parts: string[] = ['d20'];
  if (a.attackComponents?.length) {
    for (const c of a.attackComponents) {
      if (c.type === 'stat_ref' && c.statKey) {
        const desc = c.descriptor ? ` (${c.descriptor})` : '';
        parts.push(`+ ${c.statKey}${desc}`);
      } else if (c.type === 'manual') {
        const v = c.value ?? 0;
        if (v !== 0) {
          const desc = c.descriptor ? ` (${c.descriptor})` : '';
          parts.push(`${v > 0 ? '+' : ''} ${v}${desc}`);
        }
      }
    }
  }
  return parts.join(' ');
}

function formatDamageNode(d: any): string {
  const parts: string[] = [];
  if (d.diceExpr) parts.push(d.diceExpr);

  if (d.modComponents?.length) {
    for (const c of d.modComponents) {
      if (c.type === 'stat_ref' && c.statKey) {
        const desc = c.descriptor ? ` (${c.descriptor})` : '';
        parts.push(`+ ${c.statKey}${desc}`);
      } else if (c.type === 'manual') {
        const v = c.value ?? 0;
        if (v !== 0) {
          const desc = c.descriptor ? ` (${c.descriptor})` : '';
          parts.push(`${v > 0 ? '+' : ''} ${v}${desc}`);
        }
      }
    }
  }

  if (d.notes) parts.push(d.notes);

  // Weapon/creature size tag — round-trips through parser
  if (d.weaponSize) parts.push(`[size:${d.weaponSize}]`);

  // Special damage category tag — round-trips through parser
  if (d.specialType) parts.push(`[special:${d.specialType}]`);

  return parts.join(' ');
}

// ---------------------------------------------------------------------------
// Conditional bonus damage
// ---------------------------------------------------------------------------

function formatConditionalBonusNode(c: ConditionalBonusDamageNode): string {
  const lines: string[] = [];
  const { subject, op, value } = c.condition;
  const label = c.label ? ` // ${c.label}` : '';

  if (subject === 'crit_hits') {
    // Threshold is always implicit (>= 1); omit it for clarity
    lines.push(`IF CRIT_HITS {${label}`);
  } else {
    const opStr = op === 'gte' ? '>=' : op === 'lte' ? '<=' : '=';
    lines.push(`IF HITS ${opStr} ${value} {${label}`);
  }

  if (c.damage?.length) {
    const dmg = c.damage.map(formatDamageNode).join(' + ');
    lines.push(`  BONUS_DAMAGE { ${dmg} }`);
  }

  lines.push('}');
  return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Bonus attack
// ---------------------------------------------------------------------------

function formatBonusAttackNode(b: BonusAttackNode): string {
  const lines: string[] = [];
  const { subject, op, value } = b.condition;
  const label = b.label ? ` // ${b.label}` : '';

  if (subject === 'crit_hits') {
    // Threshold always implicit; omit for clarity
    lines.push(`BONUS_ATTACK when CRIT_HITS {${label}`);
  } else {
    const opStr = op === 'gte' ? '>=' : op === 'lte' ? '<=' : '=';
    lines.push(`BONUS_ATTACK when HITS ${opStr} ${value} {${label}`);
  }

  const attackLines = formatAttackNode(b.attack).split('\n');
  for (const line of attackLines) lines.push(`  ${line}`);

  lines.push('}');
  return lines.join('\n');
}
